import { LoadingOverlay } from "@/components/LoadingOverlay";
import { LoadingSmall } from "@/components/LoadingSmall";
import { Switch } from "@/components/ui/switch";
import { showApiError, showSuccess } from "@/lib/toast";
import { Role } from "@/lib/types";
import { addRoleMenu, deleteRoleMenu, getMenu, getRoleMenu } from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props {
  role: Role;
}

export const UpdateRoleMenus: React.FC<Props> = ({ role }) => {
  const { t } = useTranslation();
  const { data: menu, isLoading: isMenuLoading } = useQuery({
    queryKey: ["getMenu"],
    queryFn: () =>
      getMenu({
        numberOfObjectsPerPage: 1000,
        pageNumber: 1,
      }),
  });

  const { data: roleMenu, isLoading: isRoleMenuLoading } = useQuery({
    queryKey: ["getRoleMenu", role.id],
    queryFn: () => getRoleMenu(role.id),
  });

  const roleMenuData = roleMenu?.data?.result || [];

  const queryClient = useQueryClient();
  const deleteRoleMenuMutation = useMutation({
    mutationFn: (roleMenuId: number) => deleteRoleMenu(roleMenuId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getRoleMenu", role.id] });
      showSuccess(t("common.deletedSuccess"));
    },
    onError: showApiError,
  });

  const addRoleMenuMutation = useMutation({
    mutationFn: ({ roleId, menuId }: { roleId: number; menuId: number }) => addRoleMenu(roleId, menuId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getRoleMenu", role.id] });
      showSuccess(t("common.addedSuccess"));
    },
    onError: showApiError,
  });

  if (isMenuLoading || isRoleMenuLoading) {
    return (
      <div className="flex justify-center items-center h-full p-16">
        <LoadingSmall />
      </div>
    );
  }

  return (
    <LoadingOverlay
      isLoading={
        isMenuLoading || isRoleMenuLoading || addRoleMenuMutation.isPending || deleteRoleMenuMutation.isPending
      }
    >
      <div className="updateRoleMenusGrid">
        {menu?.data?.result?.map((menu) => {
          const roleMenu = roleMenuData.find((roleMenu) => roleMenu.menu_Id === menu.id);
          const isChecked = roleMenu ? true : false;
          return (
            <div key={menu.id} className="flex items-center gap-2 bg-muted p-2 rounded-md">
              <Switch
                checked={isChecked}
                onCheckedChange={(newChecked) => {
                  if (newChecked) {
                    addRoleMenuMutation.mutate({
                      roleId: role.id,
                      menuId: menu.id,
                    });
                  } else if (roleMenu) {
                    deleteRoleMenuMutation.mutate(roleMenu.id);
                  }
                }}
                disabled={addRoleMenuMutation.isPending || deleteRoleMenuMutation.isPending}
              />
              <span>{menu.menuName}</span>
            </div>
          );
        })}
      </div>
    </LoadingOverlay>
  );
};
